"use client";

import { useMemo, useState } from "react";
import staticData from "@/public/data/hotel/results.json";
import { Pulse } from "@/shared/components/common/Animations";
import Flex from "@/shared/components/common/Flex";
import PhosphorIcon from "@/shared/components/common/PhosphorIcon";
import Typography from "@/shared/components/common/Typography";
import { useDeviceType } from "@/shared/context/DeviceContext";
import { calculateStayNightDuration } from "@/shared/libs/date";
import { isUndefined } from "@/shared/libs/type-utils";
import type { Accommodation } from "@/types/services/hotel/apis/Accommodations";
import type { Hotel } from "@/types/services/hotel/apis/Availability";
import { CaretLeft } from "@phosphor-icons/react";
import Link, { useLinkStatus } from "next/link";

interface HotelCardPriceAndReserveProps {
  searchParams: SearchParams;
  hotel: Hotel | Accommodation;
  nextPageUrl: string;
}

export default function HotelCardPriceAndReserve({
  hotel,
  nextPageUrl,
  searchParams,
}: HotelCardPriceAndReserveProps) {
  const {
    nightLabel,
    manLabel,
    currencyLabel,
    reserveLabel,
    hotelDetailLabel,
  } = staticData.body.resultCard;
  const { pending } = useLinkStatus();
  const { checkIn, checkOut, rooms } = searchParams;
  const passengersCount = useMemo(() => {
    let passengersPerRoom: number[] = [];
    let totalPassengers: number = 0;
    rooms.split("_").map((passengers) => {
      passengersPerRoom.push(
        Number(passengers.split("-")[0]) + passengers.split("-").length - 1
      );
    });
    passengersPerRoom.map((passengers) => {
      totalPassengers += passengers;
    });
    return totalPassengers;
  }, [rooms]);
  const { min_price, total_price } = hotel;
  const isBooked = isUndefined(min_price) && isUndefined(total_price);

  const [loading, setLoading] = useState<boolean>(false);

  const handleClick = () => {
    if (!loading) {
      setLoading(true);
    }
  };

  return (
    <Flex.Item className={"max-md:w-full"}>
      <Flex
        direction={"col"}
        alignItems={"center"}
        gap={"1"}
        className="max-md:items-end"
      >
        <Flex
          direction={{
            default: "row",
            md: "col",
          }}
          alignItems={"center"}
          justifyContent={{
            default: "between",
            md: "center",
          }}
          gap={{
            default: "2",
            md: "0",
          }}
          className="max-md:bg-tertiary-10 max-md:rounded-sm max-md:px-2 max-md:py-1 w-full max-md:ring-1 max-md:ring-tertiary-20"
        >
          {!isBooked && (
            <Flex.Item>
              <Flex gap={"1"} justifyContent={"between"} alignItems={"center"}>
                <Typography
                  variant="body3"
                  fontWeight="medium"
                  className="max-md:text-primary-90"
                >
                  {calculateStayNightDuration(checkIn, checkOut)} {nightLabel} ,
                  {passengersCount} {manLabel}
                </Typography>
              </Flex>
            </Flex.Item>
          )}

          {!isBooked && (
            <Flex.Item>
              <Typography className="text-center line-through text-red-400">
                {total_price?.toLocaleString()}
              </Typography>
              <Flex alignItems={"center"} gap={"1"}>
                <Flex.Item>
                  <Typography
                    variant="body1"
                    fontWeight="semibold"
                    className="text-primary text-lg md:text-xl"
                  >
                    {min_price?.toLocaleString()}
                  </Typography>
                </Flex.Item>
                <Flex.Item>
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    className="text-primary md:text-base max-md:text-primary-80"
                  >
                    {currencyLabel}
                  </Typography>
                </Flex.Item>
              </Flex>
            </Flex.Item>
          )}
        </Flex>

        {/* - * Reserve Button * - */}

        {useDeviceType() !== "mobile" && (
          <Flex.Item className="w-full max-md:w-auto">
            <Flex direction={"col"} alignItems={"center"} gap={"1"}>
              <Link
                href={nextPageUrl}
                onClick={handleClick}
                className={`max-md:w-full ${loading ? "cursor-auto" : ""}`}
              >
                <Flex
                  alignItems={"center"}
                  justifyContent={"center"}
                  gap={"1"}
                  className={`max-md:w-full md:flex group min-w-36 min-h-10 rounded-full px-4 py-2 bg-primary hover:bg-primary-90 transition-[background] duration-100 ease-ease-in-out-circ ${loading ? "opacity-50" : ""}`}
                >
                  {loading ? (
                    <Pulse />
                  ) : (
                    <>
                      <Typography
                        variant="body1"
                        className="text-background dark:text-foreground !font-medium"
                      >
                        {isBooked ? hotelDetailLabel : reserveLabel}
                      </Typography>
                      <PhosphorIcon
                        name="CaretLeft"
                        size={20}
                        weight="regular"
                        className="text-background dark:text-foreground group-hover:-translate-x-3 transition duration-100 ease-easeInOutCirc"
                      />
                    </>
                  )}
                </Flex>
              </Link>
            </Flex>
          </Flex.Item>
        )}
      </Flex>
    </Flex.Item>
  );
}
